import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../supabase/supabase';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) throw new Error('useSubscription must be used within a SubscriptionProvider');
  return context;
};

export const SUBSCRIPTION_PLANS = {
  FREE: {
    id: 'free',
    name: 'Free',
    price: 0,
    maxVenues: 1,
    maxLandmarks: 0, // V2 Logic
    features: ['1 Venue', '0 Landmarks (SEO)', 'Basic widget']
  },
  STANDARD: {
    id: 'standard',
    name: 'Standard',
    price: 9,
    maxVenues: 2,
    maxLandmarks: 3, // V2 Logic
    features: ['Up to 2 Venues', '3 Landmarks/Venue', 'Email support']
  },
  PRO: {
    id: 'pro',
    name: 'Pro',
    price: 19,
    maxVenues: 5,
    maxLandmarks: 10, // V2 Logic
    features: ['Up to 5 Venues', '10 Landmarks/Venue', 'Priority support']
  }
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [quotas, setQuotas] = useState({ free: 0, standard: 3, pro: 10 });

  useEffect(() => {
    fetchQuotas();
    if (user) {
      // Mock activation of Free plan for new users
      setSubscription({
        plan: SUBSCRIPTION_PLANS.FREE,
        status: 'active'
      });
    }
  }, [user]);

  const fetchQuotas = async () => {
    const { data } = await supabase.from('admin_settings_1780601000000').select('*');
    if (data) {
      const newQuotas = { ...quotas };
      data.forEach(item => {
        if (item.key.startsWith('quota_landmarks_')) {
          const planKey = item.key.replace('quota_landmarks_', '');
          newQuotas[planKey] = parseInt(item.value);
        }
      });
      setQuotas(newQuotas);
    }
  };

  const upgradePlan = async (planId) => {
    const planKey = planId.toUpperCase();
    if (SUBSCRIPTION_PLANS[planKey]) {
      setSubscription({ plan: SUBSCRIPTION_PLANS[planKey], status: 'active' });
    }
  };

  const getEffectiveMaxLandmarks = () => {
    if (!subscription) return 0;
    return quotas[subscription.plan.id] || 0;
  };

  return (
    <SubscriptionContext.Provider value={{ 
      subscription, 
      upgradePlan, 
      getEffectiveMaxLandmarks,
      plans: SUBSCRIPTION_PLANS,
      refreshQuotas: fetchQuotas 
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};